import { type FC, useMemo, useState } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { Routing } from "web_myplann/Infrastructure/Routing/Routing";
import {
	Resources,
	useResource,
} from "web_myplann/Infrastructure/Translations/Resources";
import { DocumentUploadLayout } from "web_myplann/Portfolio/Components/Redesign/Documents/DocumentUploadLayout";
import { DocumentUploadSuccess } from "web_myplann/Portfolio/Components/Redesign/Documents/DocumentUploadSuccess";
import { documentLinkDetailPath } from "web_myplann/Portfolio/Helpers/documentLinkDetailPath";
import { parseDocumentUploadOrigin } from "web_myplann/Portfolio/Helpers/documentUploadOrigin";
import { UploadDocumentForm } from "web_myplann/Portfolio/Pages/Documents/UploadDocumentForm";
import type { UploadedDocument } from "web_myplann/Portfolio/Types/uploadedDocument";
import { useGoBack } from "web_myplann/Shared/Hooks/useGoBack";

const R = Resources.Portfolio.List.DocumentUpload;

export const DocumentUploadPage: FC = () => {
	const { t } = useResource();
	const navigate = useNavigate();
	const [searchParams] = useSearchParams();
	const [uploadedDocument, setUploadedDocument] =
		useState<UploadedDocument | null>(null);

	const origin = useMemo(
		() => parseDocumentUploadOrigin(searchParams),
		[searchParams],
	);
	const navigateBack = useGoBack(
		origin.link
			? documentLinkDetailPath(origin.link)
			: Routing.Portfolio.DocumentsList.path(),
	);

	const handleViewDocuments = (document: UploadedDocument) =>
		navigate(
			document.folder
				? Routing.Portfolio.FolderDetail.path(document.folder)
				: Routing.Portfolio.DocumentsList.path(),
			{ replace: true },
		);

	const handleUploaded = (document: UploadedDocument) => {
		if (origin.link) {
			navigateBack();
			return;
		}
		setUploadedDocument(document);
	};

	return (
		<DocumentUploadLayout
			title={t(uploadedDocument ? R.Success.appBarTitle : R.title)}
			onBack={navigateBack}
		>
			{uploadedDocument ? (
				<DocumentUploadSuccess
					name={uploadedDocument.name}
					folder={uploadedDocument.folder}
					linkedItemTitle={uploadedDocument.linkedItemTitle}
					onViewDocuments={() => handleViewDocuments(uploadedDocument)}
					onUploadAnother={() => setUploadedDocument(null)}
					onBack={navigateBack}
				/>
			) : (
				<UploadDocumentForm origin={origin} onUploaded={handleUploaded} />
			)}
		</DocumentUploadLayout>
	);
};
